import { DatasetComponentOption, SeriesOption } from 'echarts';
import { RealField, defaultTheme, themeMap } from '../../types';
import {
  calcGridBottom,
  getDataZoomConfig,
  getFormattedValue,
  getLegendConfig,
  getMobileDataZoomConfig,
  getXFieldConfig,
  getYFieldConfig,
} from '../util';
import { getLineChartSeries } from '../cn-line-chart/util';
import { getColumnChartSeries } from '../cn-column-chart/util';
import { ColumnChartCategory } from '../cn-column-chart/type';
import { CnDualStyle, DualRequestConfig, DualYFieldConfig } from './type';

export interface RealDualFields {
  xField: RealField[];
  leftYField: RealField[];
  rightYField: RealField[];
  categoryField?: RealField[];
}

/**
 * 配置方案一：1个横轴+多个左纵轴+多个右纵轴
 * 配置方案二：1个横轴+1个分类字段+1个左纵轴+1个右纵轴
 * @param requestConfig
 * @returns
 */
export const getDualFields = (requestConfig?: DualRequestConfig): RealDualFields => {
  return {
    xField:
      requestConfig?.metaConfig?.xField?.records?.map((record) => {
        return {
          fieldName: record.name,
          ...record.settings,
        };
      }) || [],
    leftYField:
      requestConfig?.metaConfig?.leftYField?.records?.map((record) => {
        return {
          fieldName: record.name,
          ...record.settings,
        };
      }) || [],
    rightYField:
      requestConfig?.metaConfig?.rightYField?.records?.map((record) => {
        return {
          fieldName: record.name,
          ...record.settings,
        };
      }) || [],
    categoryField: requestConfig?.metaConfig?.categoryField?.records?.map((record) => {
      return {
        fieldName: record.name,
        ...record.settings,
      };
    }),
  };
};

export function getDualChartSeries(
  listData: any[],
  dualFields: RealDualFields,
  chartStyle: CnDualStyle,
): {
  dataset: DatasetComponentOption[];
  series: SeriesOption[];
} {
  const { xField, leftYField, rightYField, categoryField } = dualFields;
  const { leftYFieldConfig, rightYFieldConfig } = chartStyle;

  function getSeriesByType(yField: RealField[], yFieldConfig?: DualYFieldConfig) {
    const { category } = yFieldConfig || {};
    const fields = { xField, yField, categoryField };
    const categoryFieldName = categoryField?.[0]?.fieldName;
    const yFieldAliasName = yField?.[0]?.aliasName;
    const style = {
      ...chartStyle,
      ...yFieldConfig,
    };

    const transformedListData = categoryFieldName
      ? listData.map((data) => {
          return {
            ...data,
            [categoryFieldName]: `${data[categoryFieldName]}-${yFieldAliasName}`,
          };
        })
      : listData;

    if (
      category === ColumnChartCategory.COLUMN ||
      category === ColumnChartCategory.STACKED_COLUMN
    ) {
      return getColumnChartSeries(transformedListData, fields, { ...style, category });
    }
    return getLineChartSeries(transformedListData, fields, style);
  }

  const { dataset: leftDataSet, series: leftSeries } = getSeriesByType(
    leftYField,
    leftYFieldConfig,
  );

  const { dataset: rightDataSet, series: rightSeries } = getSeriesByType(
    rightYField,
    rightYFieldConfig,
  );

  const transformedRightSeries = rightSeries?.map((s) => {
    return {
      ...s,
      yAxisIndex: 1,
      datasetIndex: 1,
    };
  });

  return {
    dataset: [leftDataSet, rightDataSet],
    series: [...leftSeries, ...transformedRightSeries],
  };
}

export function getDualCharOptions(chartStyle: CnDualStyle, series: SeriesOption[]) {
  const { xFieldConfig, leftYFieldConfig, rightYFieldConfig, themeColor, legendConfig } =
    chartStyle;
  const { xFieldTitle, showScrollbar = false } = xFieldConfig || {};
  const { showLegend } = legendConfig || {};
  const { yFieldTitle: originalLeftYFieldTitle } = leftYFieldConfig || {};
  const { yFieldTitle: originalRightYFieldTitle } = rightYFieldConfig || {};

  const leftYFieldTitle = getFormattedValue(originalLeftYFieldTitle) as string;
  const rightYFieldTitle = getFormattedValue(originalRightYFieldTitle) as string;

  const offsetLeft = leftYFieldTitle ? 24 : 0;
  const offsetRight = rightYFieldTitle ? 24 : 0;

  const legendData = series.map((s) => {
    return {
      name: s.name as string,
      icon: s.type === 'line' ? 'path://M0,-1 L10,-1 L10,1 L0,1 Z' : '',
    };
  });

  return {
    color: themeMap[themeColor || defaultTheme].color,
    ...getDataZoomConfig(showScrollbar),
    legend: getLegendConfig(showLegend, {
      data: legendData,
      left: offsetLeft,
    }),
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: getXFieldConfig(xFieldConfig),
    yAxis: [getYFieldConfig(leftYFieldConfig), getYFieldConfig(rightYFieldConfig)],
    grid: {
      top: showLegend ? 32 : 12,
      left: offsetLeft,
      right: offsetRight,
      bottom: calcGridBottom(xFieldTitle, showScrollbar),
      containLabel: true,
    },
    media: [
      {
        query: { maxWidth: 500 },
        option: {
          ...getMobileDataZoomConfig(showScrollbar),
          tooltip: { triggerOn: 'click' },
        },
      },
    ],
  };
}
